import React from "react";
import { useTranslation } from "react-i18next";
import { Button, Card, Popconfirm, Segmented, Space, Table, Tag, Tooltip, Typography } from "antd";
import type { ColumnType } from "antd/es/table";
import { DeleteOutlined, EditOutlined, PlusOutlined } from "@ant-design/icons";

import { API_URL } from "../../shared/utils/Util";
import { getFetchT } from "../../shared/utils/TypedUtil";
import { useAntdTheme, useNotification } from "../../shared/hooks";
import { GetMenuResponse } from "../../types/api";
import { ItemSchemaGroup, MenuType } from "../../types/model";
import { PointsConfig } from "../points-config";
import { deleteComboAddonSchema, getComboAddonSchemas, getPointsConfig } from "./api";
import { ComboAddonModal, usedByNames } from "./ComboAddonModal";
import { MENU_TYPES, menuTypeLabel, tierLabel } from "./components";
import { formatCents } from "./pricing";
import { ComboAddonSchemaWithUsage } from "./types";

const { Text } = Typography;

type ComboAddonListProps = {
  restaurantId: string;
};

/** The "Combo add-ons" page: reusable add-ons (e.g. "Drinks") per menu type. */
export const ComboAddonList: React.FC<ComboAddonListProps> = ({ restaurantId }) => {
  const { t } = useTranslation();
  const { token } = useAntdTheme();
  const { deleteSuccess, notifyError } = useNotification();

  const [menuType, setMenuType] = React.useState<MenuType>("dine_in");
  const [addons, setAddons] = React.useState<ComboAddonSchemaWithUsage[] | undefined>(undefined);
  const [groups, setGroups] = React.useState<ItemSchemaGroup[]>([]);
  const [pointsConfig, setPointsConfig] = React.useState<PointsConfig | undefined>(undefined);
  const [editing, setEditing] = React.useState<ComboAddonSchemaWithUsage | null | undefined>(
    undefined
  );
  const [deleting, setDeleting] = React.useState<string | null>(null);

  const refresh = React.useCallback(() => {
    setAddons(undefined);
    getComboAddonSchemas(
      restaurantId,
      menuType,
      (result) => setAddons([...result].sort((a, b) => a.name.localeCompare(b.name))),
      (err) => {
        notifyError(t("combo_failed_to_load_addons"), err);
        setAddons([]);
      }
    );
  }, [restaurantId, menuType]);

  React.useEffect(refresh, [refresh]);

  React.useEffect(() => {
    getFetchT<GetMenuResponse>(
      `${API_URL}/v1/r/${restaurantId}/menu`,
      (result) => setGroups(result.item_schema_groups ?? []),
      (err) => notifyError(t("error"), err)
    );
    getPointsConfig(restaurantId, setPointsConfig);
  }, [restaurantId]);

  const handleDelete = (addon: ComboAddonSchemaWithUsage) => {
    setDeleting(addon.id);
    deleteComboAddonSchema(
      restaurantId,
      addon.id,
      () => {
        setDeleting(null);
        deleteSuccess(addon.name);
        refresh();
      },
      (err) => {
        setDeleting(null);
        notifyError(t("combo_failed_to_delete_addon"), err);
      }
    );
  };

  const columns: ColumnType<ComboAddonSchemaWithUsage>[] = [
    {
      title: t("name"),
      dataIndex: "name",
      render: (_: unknown, addon) => (
        <Button type="link" style={{ padding: 0 }} onClick={() => setEditing(addon)}>
          {addon.name}
        </Button>
      ),
    },
    {
      title: t("combo_tiers"),
      render: (_: unknown, addon) => (
        <Space size={[4, 4]} wrap>
          {[...addon.tiers]
            .sort((a, b) => a.seq - b.seq)
            .map((tier) => (
              <Tag key={tier.id}>
                {tierLabel(t, tier)}
                {(tier.name ?? "").trim() !== "" ? ` · ${formatCents(tier.price)}` : ""}
                {` (${tier.items?.length ?? 0})`}
              </Tag>
            ))}
        </Space>
      ),
    },
    {
      title: t("combo_used_by"),
      width: 200,
      render: (_: unknown, addon) =>
        addon.used_by.length === 0 ? (
          <Text type="secondary">{t("combo_not_used")}</Text>
        ) : (
          <Tooltip title={usedByNames(addon)}>
            <Text data-testid="combo-addon-used-by">
              {t("combo_used_by_n_combos", { count: addon.used_by.length })}
            </Text>
          </Tooltip>
        ),
    },
    {
      title: "",
      width: 110,
      align: "right" as const,
      render: (_: unknown, addon) => (
        <Space size={0}>
          <Button
            type="text"
            aria-label={t("edit")}
            icon={<EditOutlined />}
            onClick={() => setEditing(addon)}
          />
          {addon.used_by.length > 0 ? (
            <Tooltip title={t("combo_addon_delete_in_use", { combos: usedByNames(addon) })}>
              <Button type="text" aria-label={t("delete")} icon={<DeleteOutlined />} disabled />
            </Tooltip>
          ) : (
            <Popconfirm
              title={t("combo_delete_addon_confirm", { name: addon.name })}
              okText={t("yes")}
              cancelText={t("no")}
              onConfirm={() => handleDelete(addon)}
            >
              <Button
                type="text"
                danger
                aria-label={t("delete")}
                icon={<DeleteOutlined />}
                loading={deleting === addon.id}
              />
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  return (
    <Card
      style={{ borderRadius: token.borderRadiusLG, border: `1px solid ${token.colorBorder}` }}
      styles={{ body: { padding: token.paddingLG } }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: token.marginSM,
          marginBottom: token.marginLG,
        }}
      >
        <Segmented<MenuType>
          value={menuType}
          onChange={setMenuType}
          options={MENU_TYPES.map((mt) => ({ value: mt, label: menuTypeLabel(t, mt) }))}
        />
        <Button type="primary" icon={<PlusOutlined />} onClick={() => setEditing(null)}>
          {t("combo_create_addon")}
        </Button>
      </div>
      <Text type="secondary" style={{ display: "block", marginBottom: token.marginMD }}>
        {t("combo_addons_description")}
      </Text>
      <Table
        columns={columns}
        dataSource={addons ?? []}
        loading={addons === undefined}
        rowKey={(addon) => addon.id}
        pagination={false}
        locale={{ emptyText: t("combo_no_addons") }}
      />
      <ComboAddonModal
        open={editing !== undefined}
        restaurantId={restaurantId}
        addon={editing ?? undefined}
        defaultMenuType={menuType}
        groups={groups}
        pointsConfig={pointsConfig}
        onClose={() => setEditing(undefined)}
        onSaved={(saved) => {
          setEditing(undefined);
          if (saved.menu_type !== menuType) {
            setMenuType(saved.menu_type);
          } else {
            refresh();
          }
        }}
      />
    </Card>
  );
};
